import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageCircle, AlertTriangle, Clock3, Phone } from "lucide-react";
import { api, apiErrorMessage } from "../lib/api";
import { currencyTRY, dateTR } from "../lib/format";

/** Normalize a Turkish phone to E.164 without leading +: +90 5xx xxx xx xx → 905xxxxxxxxx */
export function normalizePhone(raw) {
  if (!raw) return "";
  const d = String(raw).replace(/\D/g, "");
  if (!d) return "";
  if (d.startsWith("00")) return d.slice(2);
  if (d.startsWith("90")) return d;
  // Turkish mobile like 0532... → 90532...
  if (d.startsWith("0") && d.length >= 10) return "90" + d.slice(1);
  if (d.length === 10 && d.startsWith("5")) return "90" + d;
  return d;
}

export function whatsappUrl(phone, text) {
  const p = normalizePhone(phone);
  if (!p) return "";
  return `https://wa.me/${p}?text=${encodeURIComponent(text)}`;
}

function reminderText(r) {
  const when =
    r.when === "overdue"
      ? "iade tarihi geçti"
      : r.when === "today"
        ? "aracı bugün iade etmeniz gerekiyor"
        : "aracı yarın iade etmeniz gerekiyor";
  const kalan = Math.max(r.toplamTutar - (r.odenenTutar || 0), 0);
  const kalanCumle = kalan > 0 ? ` Kalan ödeme: ${currencyTRY(kalan)}.` : "";
  return `Merhaba ${r.musteriAdi}, ${r.aracMarka} ${r.aracModel} (${r.plaka}) kiralamanız için ${when}. İade tarihi: ${dateTR(r.bitisTarihi)}.${kalanCumle} İletişim için buradan yazabilirsiniz. Teşekkürler — Oto Galeri`;
}

function Row({ r }) {
  const wa = whatsappUrl(r.musteriTelefon, reminderText(r));
  const tone =
    r.when === "overdue"
      ? "border-rose-200 bg-rose-50/50"
      : r.when === "today"
        ? "border-amber-200 bg-amber-50/50"
        : "border-stone-200 bg-white";
  const label = {
    overdue: (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-800 bg-rose-100 rounded-full px-2 py-0.5">
        <AlertTriangle className="w-3 h-3" /> Gecikmiş
      </span>
    ),
    today: (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-800 bg-amber-100 rounded-full px-2 py-0.5">
        <Clock3 className="w-3 h-3" /> Bugün
      </span>
    ),
    tomorrow: (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-700 bg-stone-200 rounded-full px-2 py-0.5">
        Yarın
      </span>
    ),
  }[r.when];

  return (
    <div
      className={`rounded-xl border p-3 flex flex-wrap items-center gap-3 ${tone}`}
      data-testid={`reminder-${r.id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-slate-900 truncate">
            {r.musteriAdi}
          </span>
          {label}
        </div>
        <div className="text-xs text-stone-500 mt-0.5 truncate">
          {r.aracMarka} {r.aracModel} · {r.plaka} · İade: {dateTR(r.bitisTarihi)}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {r.musteriTelefon && (
          <a
            href={`tel:${r.musteriTelefon}`}
            className="h-9 w-9 rounded-full border border-stone-300 text-stone-700 hover:bg-stone-50 flex items-center justify-center"
            title="Ara"
          >
            <Phone className="w-4 h-4" />
          </a>
        )}
        {wa ? (
          <a
            href={wa}
            target="_blank"
            rel="noreferrer"
            data-testid={`wa-${r.id}`}
            className="h-9 px-3 rounded-full bg-[#25D366] hover:bg-[#20BD5B] text-white text-sm font-semibold flex items-center gap-1.5"
          >
            <MessageCircle className="w-4 h-4" /> WhatsApp
          </a>
        ) : (
          <span className="text-xs text-stone-400">Telefon yok</span>
        )}
      </div>
    </div>
  );
}

export default function Reminders() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/rentals/reminders");
        setList(res.data);
      } catch (e) {
        toast.error(apiErrorMessage(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return null;

  return (
    <div
      className="rounded-2xl border border-stone-200 bg-white p-5 md:p-6"
      data-testid="reminders-card"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-widest text-stone-500">
            İade Hatırlatmaları
          </div>
          <h3 className="font-display font-black text-xl text-slate-900 mt-1">
            Bugün & Yarın
          </h3>
        </div>
        <span className="text-xs text-stone-500">
          Tıkla → WhatsApp hazır mesajla açılır
        </span>
      </div>
      {list.length === 0 ? (
        <div className="text-sm text-stone-500 py-6 text-center">
          Bugün veya yarın iade edilecek kiralama yok.
        </div>
      ) : (
        <div className="space-y-2">
          {list.map((r) => (
            <Row key={r.id} r={r} />
          ))}
        </div>
      )}
    </div>
  );
}
